import * as Sentry from '@sentry/browser';

/**
 * Constructs a Salesforce Case creation extension for Voiceflow.
 *
 * @function
 * @param {Object} firebase - The Firebase instance.
 * @param {Object} options - Configuration options.
 * @param {string} options.orgId - The Salesforce organization ID.
 * @param {string} options.userEmail - The email address of the user creating the case.
 * @returns {Promise<Object>} A promise that resolves to the Voiceflow Extension object for creating Salesforce cases.
 *
 * @throws {Error} If firebase, orgId, or userEmail is not provided.
 *
 * @example
 * const salesforceCreateCaseExtension = await ConstructCreateSalesforceCaseExtension(firebase, { orgId, userEmail });
 */
export const ConstructCreateSalesforceCaseExtension = async (firebase, { orgId, userEmail }) => {
  if (!firebase) throw new Error('Firebase instance is required for Salesforce extension');
  if (!orgId) throw new Error('orgId is required for Salesforce extension');
  if (!userEmail) throw new Error('userEmail is required for Salesforce extension');

  // Function to create a Salesforce case using the backend API
  const createSalesforceCase = async ({ subject, description, priority }) => {
    const agent = await firebase.getAgent();
    const requestData = {
      orgId,
      subject,
      description,
      priority,
      userEmail,
    };
    return agent.post('/salesforce/cases').send(requestData);
  };

  // Return Voiceflow Extension object
  return {
    name: 'SalesforceExtension',
    type: 'effect',
    match: ({ trace }) => {
      return trace.type === 'Create Salesforce Case';
    },
    effect: async ({ trace }) => {
      // Extract case data from the trace payload
      // If payload is a string, try to parse it safely
      let caseData = {};
      if (typeof trace.payload === 'string') {
        try {
          // Replace only invalid control characters (except \t, \n, \r) with space
          const sanitizedPayload = trace.payload.replace(/[^\S\t\n\r ]/g, ' ');
          caseData = JSON.parse(sanitizedPayload);
        } catch (e) {
          console.error('Error parsing trace.payload:', e, trace.payload);
          Sentry.withScope((scope) => {
            scope.setTag('feature', 'AI_Chatbot');
            scope.setExtra('tracePayload', trace.payload);
            Sentry.captureException(e);
          });
          return;
        }
      } else if (typeof trace.payload === 'object' && trace.payload !== null) {
        caseData = trace.payload;
      } else {
        console.error('Invalid trace.payload format:', trace.payload);
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'AI_Chatbot');
          scope.setExtra('tracePayload', trace.payload);
          Sentry.captureMessage('Invalid trace.payload format');
        });
        return;
      }

      try {
        // Attempt to create the Salesforce case
        const result = await createSalesforceCase(caseData);
        if (result.body.success) {
          // Send success event back to Voiceflow
          window.voiceflow.chat.interact({
            type: 'sf_case_created',
            payload: {
              caseId: result.body.id,
              message: `Case created successfully! Case ID: ${result.body.id}`,
            },
          });
        } else {
          console.error('Error creating Salesforce case:', result.body.errors);
          Sentry.withScope((scope) => {
            scope.setTag('feature', 'AI_Chatbot');
            scope.setExtra('caseData', caseData);
            scope.setExtra('responseBody', result.body);
            Sentry.captureMessage('Salesforce case creation failed');
          });
          // Send error event back to Voiceflow
          window.voiceflow.chat.interact({
            type: 'sf_case_error',
            payload: {
              message: `Failed to create case: ${result.body.errors.toString()}`,
            },
          });
        }
      } catch (error) {
        // Handle unexpected errors
        console.error('Unexpected error creating Salesforce case:', error);
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'AI_Chatbot');
          scope.setExtra('caseData', caseData);
          Sentry.captureException(error);
        });
        window.voiceflow.chat.interact({
          type: 'sf_case_error',
          payload: {
            error: error.message,
            message: `Unexpected error: ${error.message}`,
          },
        });
      }
    },
  };
};
